import { EyeIcon, MoreVerticalIcon, RefreshCwIcon } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { queryClient } from "@/lib/react-query";

import { useResetRecordingExpiration } from "../api/reset-recording-expiration";

type RecordingStorageActionsProps = {
  recordingId: string;
  recordingName: string;
};

export function RecordingStorageActions({
  recordingId,
  recordingName,
}: RecordingStorageActionsProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const {
    mutate: resetRecordingExpiration,
    isPending: isResettingRecordingExpiration,
    isError: isResettingRecordingExpirationError,
    error: resettingRecordingExpirationError,
  } = useResetRecordingExpiration();

  const handleRestore = () => {
    resetRecordingExpiration(
      { id: recordingId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            predicate: (query) => {
              // Delete all recording queries for this ID
              const key = query?.queryKey;

              // Filter for recording queries
              if (!key.includes("recordings") && !key.includes("recording")) {
                return false;
              }

              return true;
            },
          });

          setIsDialogOpen(false);
        },
      },
    );
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVerticalIcon className="size-4" aria-hidden={true} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem>
            <Link
              to={`/recordings/${recordingId}`}
              target="_blank"
              className="flex items-center gap-2"
            >
              <EyeIcon className="size-4" aria-hidden={true} />
              View Recording
            </Link>
          </DropdownMenuItem>
          <DialogTrigger asChild>
            <DropdownMenuItem className="cursor-pointer text-destructive">
              <RefreshCwIcon className="size-4" aria-hidden={true} />
              Restore recording
            </DropdownMenuItem>
          </DialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Are you sure you want to restore {recordingName}?
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="text-sm text-muted-foreground">
          <p>
            The recording's expiration timer will be reset, causing it to be
            available for more time based on the organisation's license.
          </p>
          {isResettingRecordingExpirationError && (
            <Alert variant="destructive" className="mt-4">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                {resettingRecordingExpirationError.message}
              </AlertDescription>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={handleRestore}
            disabled={isResettingRecordingExpiration}
            isAnimated
            isLoading={isResettingRecordingExpiration}
          >
            Restore
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
